import React, { useState, useEffect } from 'react';
import CalcShell from '../CalcShell';
import { CNetResult, CNetHintBar, G } from '../other/_cnet';

/* Sub 1 — 2D distance */
function TwoDSection() {
  const [x1, sX1] = useState(3), [y1, sY1] = useState(5);
  const [x2, sX2] = useState(9), [y2, sY2] = useState(15);
  const [res, setRes] = useState(null);
  const calc = () => {
    const dx = Number(x2) - Number(x1), dy = Number(y2) - Number(y1);
    const d = Math.sqrt(dx * dx + dy * dy);
    setRes({ dx, dy, d, x1: +x1, y1: +y1, x2: +x2, y2: +y2 });
  };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [x1, y1, x2, y2]);
  return (
    <section>
      <h3 className="cnet-section-title">Distance between two points in 2D</h3>
      <div className="cnet-panel">
        <div className="grid grid-cols-4 gap-3">
          <div><div className="text-center font-semibold mb-1">X<sub>1</sub></div><input data-testid="d-2d-x1" className="cnet-input w-full" type="number" value={x1} onChange={e => sX1(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Y<sub>1</sub></div><input data-testid="d-2d-y1" className="cnet-input w-full" type="number" value={y1} onChange={e => sY1(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">X<sub>2</sub></div><input data-testid="d-2d-x2" className="cnet-input w-full" type="number" value={x2} onChange={e => sX2(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Y<sub>2</sub></div><input data-testid="d-2d-y2" className="cnet-input w-full" type="number" value={y2} onChange={e => sY2(e.target.value)} /></div>
        </div>
      </div>
      {res && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="d-2d-result">
            Distance = <span className="text-[#4c7b25]">{G(res.d, 10)}</span>
          </div>
          <div className="mt-2 font-mono text-sm leading-7">
            <div>d = √((x₂ − x₁)² + (y₂ − y₁)²)</div>
            <div>= √(({res.x2} − {res.x1})² + ({res.y2} − {res.y1})²)</div>
            <div>= √({G(res.dx * res.dx, 4)} + {G(res.dy * res.dy, 4)})</div>
            <div>= <b className="text-[#4c7b25]">{G(res.d, 10)}</b></div>
          </div>
        </CNetResult>
      )}
      {res && (() => {
        const W = 300, H = 220, pad = 20;
        const xs = [res.x1, res.x2, 0], ys = [res.y1, res.y2, 0];
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const rangeX = Math.max(maxX - minX, 1), rangeY = Math.max(maxY - minY, 1);
        const tx = x => pad + ((x - minX) / rangeX) * (W - 2 * pad);
        const ty = y => H - pad - ((y - minY) / rangeY) * (H - 2 * pad);
        return (
          <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="d-2d-graph">
            <div className="text-xs font-semibold text-slate-600 mb-1">Graph</div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md">
              <line x1={pad} y1={ty(0)} x2={W - pad} y2={ty(0)} stroke="#94a3b8" />
              <line x1={tx(0)} y1={pad} x2={tx(0)} y2={H - pad} stroke="#94a3b8" />
              <line x1={tx(res.x1)} y1={ty(res.y1)} x2={tx(res.x2)} y2={ty(res.y2)} stroke="#2563eb" strokeWidth="2" />
              <line x1={tx(res.x1)} y1={ty(res.y1)} x2={tx(res.x2)} y2={ty(res.y1)} stroke="#dc2626" strokeDasharray="3 3" />
              <line x1={tx(res.x2)} y1={ty(res.y1)} x2={tx(res.x2)} y2={ty(res.y2)} stroke="#dc2626" strokeDasharray="3 3" />
              <circle cx={tx(res.x1)} cy={ty(res.y1)} r="4" fill="#059669" />
              <circle cx={tx(res.x2)} cy={ty(res.y2)} r="4" fill="#dc2626" />
              <text x={tx(res.x1) + 6} y={ty(res.y1) - 6} fontSize="11" fill="#059669">P₁({res.x1},{res.y1})</text>
              <text x={tx(res.x2) + 6} y={ty(res.y2) - 6} fontSize="11" fill="#dc2626">P₂({res.x2},{res.y2})</text>
            </svg>
          </div>
        );
      })()}
    </section>
  );
}

/* Sub 2 — 3D distance */
function ThreeDSection() {
  const [x1, sX1] = useState(1), [y1, sY1] = useState(2), [z1, sZ1] = useState(3);
  const [x2, sX2] = useState(4), [y2, sY2] = useState(6), [z2, sZ2] = useState(8);
  const [res, setRes] = useState(null);
  const calc = () => {
    const dx = Number(x2) - Number(x1), dy = Number(y2) - Number(y1), dz = Number(z2) - Number(z1);
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    setRes({ dx, dy, dz, d, x1: +x1, y1: +y1, z1: +z1, x2: +x2, y2: +y2, z2: +z2 });
  };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [x1, y1, z1, x2, y2, z2]);
  return (
    <section className="mt-4">
      <h3 className="cnet-section-title">Distance between two points in 3D</h3>
      <div className="cnet-panel">
        <div className="grid grid-cols-3 gap-3">
          {[['X₁', x1, sX1], ['Y₁', y1, sY1], ['Z₁', z1, sZ1], ['X₂', x2, sX2], ['Y₂', y2, sY2], ['Z₂', z2, sZ2]].map(([lbl, v, s], i) => (
            <div key={i}><div className="text-center font-semibold mb-1">{lbl}</div><input className="cnet-input w-full" type="number" value={v} onChange={e => s(e.target.value)} /></div>
          ))}
        </div>
      </div>
      {res && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="d-3d-result">
            Distance = <span className="text-[#4c7b25]">{G(res.d, 10)}</span>
          </div>
          <div className="mt-2 font-mono text-sm">d = √(Δx² + Δy² + Δz²) = √({G(res.dx * res.dx, 3)} + {G(res.dy * res.dy, 3)} + {G(res.dz * res.dz, 3)}) = <b className="text-[#4c7b25]">{G(res.d, 10)}</b></div>
        </CNetResult>
      )}
      {res && (() => {
        const W = 340, H = 240, pad = 30;
        const proj = (x, y, z) => [x - z * 0.5, y - z * 0.4];
        const p1 = proj(res.x1, res.y1, res.z1);
        const p2 = proj(res.x2, res.y2, res.z2);
        const o = proj(0, 0, 0);
        const xs = [p1[0], p2[0], o[0]], ys = [p1[1], p2[1], o[1]];
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const rangeX = Math.max(maxX - minX, 1), rangeY = Math.max(maxY - minY, 1);
        const tx = x => pad + ((x - minX) / rangeX) * (W - 2 * pad);
        const ty = y => H - pad - ((y - minY) / rangeY) * (H - 2 * pad);
        return (
          <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="d-3d-graph">
            <div className="text-xs font-semibold text-slate-600 mb-1">3D Graph (isometric projection)</div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md">
              {[[10, 0, 0], [0, 10, 0], [0, 0, 10]].map((v, i) => {
                const p = proj(...v);
                return <line key={i} x1={tx(o[0])} y1={ty(o[1])} x2={tx(p[0])} y2={ty(p[1])} stroke="#94a3b8" strokeDasharray="2 2" />;
              })}
              <text x={tx(proj(10, 0, 0)[0]) + 4} y={ty(proj(10, 0, 0)[1])} fontSize="10" fill="#64748b">+x</text>
              <text x={tx(proj(0, 10, 0)[0])} y={ty(proj(0, 10, 0)[1]) - 4} fontSize="10" fill="#64748b">+y</text>
              <text x={tx(proj(0, 0, 10)[0]) - 6} y={ty(proj(0, 0, 10)[1]) - 4} fontSize="10" fill="#64748b">+z</text>
              <line x1={tx(p1[0])} y1={ty(p1[1])} x2={tx(p2[0])} y2={ty(p2[1])} stroke="#2563eb" strokeWidth="2" />
              <circle cx={tx(p1[0])} cy={ty(p1[1])} r="4" fill="#059669" />
              <circle cx={tx(p2[0])} cy={ty(p2[1])} r="4" fill="#dc2626" />
              <text x={tx(p1[0]) + 6} y={ty(p1[1]) - 6} fontSize="11" fill="#059669">P₁({res.x1},{res.y1},{res.z1})</text>
              <text x={tx(p2[0]) + 6} y={ty(p2[1]) - 6} fontSize="11" fill="#dc2626">P₂({res.x2},{res.y2},{res.z2})</text>
            </svg>
          </div>
        );
      })()}
    </section>
  );
}

/* Sub 3 — Haversine distance */
function LatLonSection() {
  const [lat1, sLat1] = useState(40.7128), [lon1, sLon1] = useState(-74.006); // NY
  const [lat2, sLat2] = useState(34.0522), [lon2, sLon2] = useState(-118.2437); // LA
  const [res, setRes] = useState(null);
  const calc = () => {
    const R = 6371; // km
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(Number(lat2) - Number(lat1));
    const dLon = toRad(Number(lon2) - Number(lon1));
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(+lat1)) * Math.cos(toRad(+lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const km = R * c;
    setRes({ km, miles: km * 0.621371, nauticalMiles: km * 0.539957 });
  };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lat1, lon1, lat2, lon2]);
  return (
    <section className="mt-4">
      <h3 className="cnet-section-title">Distance based on Latitude and Longitude</h3>
      <div className="cnet-panel">
        <div className="grid grid-cols-2 gap-3">
          <div><div className="text-center font-semibold mb-1">Point 1 Latitude</div><input className="cnet-input w-full" type="number" step="any" value={lat1} onChange={e => sLat1(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Point 1 Longitude</div><input className="cnet-input w-full" type="number" step="any" value={lon1} onChange={e => sLon1(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Point 2 Latitude</div><input className="cnet-input w-full" type="number" step="any" value={lat2} onChange={e => sLat2(e.target.value)} /></div>
          <div><div className="text-center font-semibold mb-1">Point 2 Longitude</div><input className="cnet-input w-full" type="number" step="any" value={lon2} onChange={e => sLon2(e.target.value)} /></div>
        </div>
      </div>
      {res && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="d-ll-result">
            <tbody>
              <tr><td>Distance (kilometers)</td><td><b className="text-[#4c7b25]">{G(res.km, 4)}</b> km</td></tr>
              <tr><td>Distance (miles)</td><td><b className="text-[#4c7b25]">{G(res.miles, 4)}</b> mi</td></tr>
              <tr><td>Distance (nautical miles)</td><td><b className="text-[#4c7b25]">{G(res.nauticalMiles, 4)}</b> NM</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </section>
  );
}

export default function DistanceCalc() {
  return (
    <CalcShell showActions={false} look="cnet" title="Distance Calculator" subtitle="Compute the distance between points in 2D, 3D, or on Earth using latitude and longitude.">
      <CNetHintBar />
      <TwoDSection />
      <ThreeDSection />
      <LatLonSection />
    </CalcShell>
  );
}
